import { createTheme } from '@mui/material/styles';
import type { Theme, ThemeOptions, TypographyStyle } from '@mui/material/styles';
import { createSpacing } from '@mui/system';

import { adjustLightness, contrastRatio, meetsWcagAA } from '@/styles/theme/color-utils';
import type { AccessibilityContrast, AccessibilityPreferences } from './types';

// The full set of typography variant keys this app's own theme (see
// create-unit-site-theme.ts) actually sets per-role fonts on — kept in sync
// with that list so text-size/spacing scaling covers everything a block
// component might render.
const TYPOGRAPHY_VARIANT_KEYS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'subtitle1', 'subtitle2', 'body1', 'body2', 'button', 'caption', 'overline',
] as const;

const TEXT_SIZE_FACTOR: Record<AccessibilityPreferences['textSize'], number> = {
  default: 1,
  large: 1.15,
  'extra-large': 1.3,
};

// Additive deltas, not multipliers — letterSpacing/wordSpacing start at or
// near zero in this app's theme (create-unit-site-theme.ts never sets
// them), so a multiplier would have no effect on a zero base.
const TEXT_SPACING_LETTER_DELTA_EM: Record<AccessibilityPreferences['textSpacing'], number> = {
  default: 0,
  increased: 0.06,
  maximum: 0.12,
};
const TEXT_SPACING_WORD_DELTA_EM: Record<AccessibilityPreferences['textSpacing'], number> = {
  default: 0,
  increased: 0.08,
  maximum: 0.16,
};
const TEXT_SPACING_LINE_HEIGHT_FACTOR: Record<AccessibilityPreferences['textSpacing'], number> = {
  default: 1,
  increased: 1.15,
  maximum: 1.4,
};

const CONTENT_SPACING_FACTOR: Record<AccessibilityPreferences['contentSpacing'], number> = {
  default: 1,
  comfortable: 1.25,
  expanded: 1.5,
};

const READABLE_FONT_STACK = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

// Parses a CSS length ("1.5rem", "14px", or a bare number treated as px)
// and returns it scaled by `factor` in its original unit. Returns the
// input unchanged (as a string) if it can't be parsed, so an unexpected
// value never throws.
function scaleCssLength(value: TypographyStyle['fontSize'], factor: number): TypographyStyle['fontSize'] {
  if (value == null || factor === 1) return value;
  if (typeof value === 'number') return value * factor;
  const match = /^(-?[\d.]+)([a-z%]*)$/i.exec(value.trim());
  if (!match) return value;
  const [, numeric, unit] = match;
  return `${(parseFloat(numeric) * factor).toFixed(4)}${unit}`;
}

function addEm(value: TypographyStyle['letterSpacing'], deltaEm: number): TypographyStyle['letterSpacing'] {
  if (deltaEm === 0) return value;
  const current = typeof value === 'number' ? value : typeof value === 'string' ? parseFloat(value) || 0 : 0;
  return `${(current + deltaEm).toFixed(4)}em`;
}

function scaleLineHeight(value: TypographyStyle['lineHeight'], factor: number): TypographyStyle['lineHeight'] {
  if (factor === 1 || value == null) return value;
  const current = typeof value === 'number' ? value : parseFloat(String(value));
  if (Number.isNaN(current)) return value;
  return Number((current * factor).toFixed(3));
}

function scaleTypography(
  base: Theme['typography'],
  sizeFactor: number,
  letterDeltaEm: number,
  lineHeightFactor: number,
) {
  const overrides: Record<string, Partial<TypographyStyle>> = {};
  for (const key of TYPOGRAPHY_VARIANT_KEYS) {
    const variant = base[key];
    overrides[key] = {
      fontSize: scaleCssLength(variant.fontSize, sizeFactor),
      letterSpacing: addEm(variant.letterSpacing, letterDeltaEm),
      lineHeight: scaleLineHeight(variant.lineHeight, lineHeightFactor),
    };
  }
  return overrides;
}

// Nudges `hex` toward or away from black/white (whichever direction raises
// contrast against `background`) until it clears the WCAG AA threshold, so
// a unit's brand accent/status colors stay recognizable but readable in a
// high-contrast palette rather than being replaced outright.
function ensureContrast(hex: string, background: string, isLargeOrUi = false): string {
  let candidate = hex;
  if (meetsWcagAA(contrastRatio(candidate, background), isLargeOrUi)) return candidate;
  const backgroundIsLight = contrastRatio(background, '#ffffff') < contrastRatio(background, '#000000');
  const step = backgroundIsLight ? -0.06 : 0.06;
  for (let i = 0; i < 20 && !meetsWcagAA(contrastRatio(candidate, background), isLargeOrUi); i += 1) {
    candidate = adjustLightness(candidate, step * (i + 1));
  }
  return meetsWcagAA(contrastRatio(candidate, background), isLargeOrUi) ? candidate : (backgroundIsLight ? '#000000' : '#ffffff');
}

function contrastPaletteOverride(baseTheme: Theme, mode: AccessibilityContrast) {
  if (mode === 'default') return undefined;
  const background = mode === 'high-contrast-light' ? '#ffffff' : '#000000';
  const text = mode === 'high-contrast-light' ? '#000000' : '#ffffff';
  return {
    mode: mode === 'high-contrast-light' ? ('light' as const) : ('dark' as const),
    background: { default: background, paper: background },
    text: { primary: text, secondary: text },
    divider: text,
    primary: { main: ensureContrast(baseTheme.palette.primary.main, background) },
    secondary: { main: ensureContrast(baseTheme.palette.secondary.main, background) },
    success: { main: ensureContrast(baseTheme.palette.success.main, background) },
    warning: { main: ensureContrast(baseTheme.palette.warning.main, background) },
    error: { main: ensureContrast(baseTheme.palette.error.main, background) },
    info: { main: ensureContrast(baseTheme.palette.info.main, background) },
  };
}

function spacingUnitPx(theme: Theme): number {
  const oneUnit = theme.spacing(1);
  const match = /^([\d.]+)px$/.exec(String(oneUnit));
  return match ? parseFloat(match[1]) : 8;
}

// Derives a visitor-facing "effective" theme from the unit's own saved
// theme — never re-derives from raw SiteThemeConfig, and every default
// preference must leave `baseTheme` byte-for-byte equivalent (see
// apply-accessibility-preferences.test.ts's regression assertion).
export function applyAccessibilityPreferences(baseTheme: Theme, preferences: AccessibilityPreferences): Theme {
  const sizeFactor = TEXT_SIZE_FACTOR[preferences.textSize];
  const letterDeltaEm = TEXT_SPACING_LETTER_DELTA_EM[preferences.textSpacing];
  const wordDeltaEm = TEXT_SPACING_WORD_DELTA_EM[preferences.textSpacing];
  const lineHeightFactor = TEXT_SPACING_LINE_HEIGHT_FACTOR[preferences.textSpacing];
  const contentSpacingFactor = CONTENT_SPACING_FACTOR[preferences.contentSpacing];

  const typographyOverride = scaleTypography(baseTheme.typography, sizeFactor, letterDeltaEm, lineHeightFactor);
  if (preferences.readableFont) {
    for (const key of TYPOGRAPHY_VARIANT_KEYS) {
      typographyOverride[key] = { ...typographyOverride[key], fontFamily: READABLE_FONT_STACK };
    }
  }

  const paletteOverride = contrastPaletteOverride(baseTheme, preferences.contrast);
  const focusRingColor = paletteOverride
    ? ensureContrast(baseTheme.palette.primary.main, paletteOverride.background.default)
    : ensureContrast('#1a73e8', baseTheme.palette.background.default);

  const components: ThemeOptions['components'] = {};
  if (wordDeltaEm > 0) {
    components.MuiTypography = { styleOverrides: { root: { wordSpacing: `${wordDeltaEm.toFixed(4)}em` } } };
  }
  if (preferences.highlightLinks) {
    components.MuiLink = { defaultProps: { underline: 'always' } };
  }
  if (preferences.enhancedFocus) {
    components.MuiCssBaseline = {
      styleOverrides: {
        '*:focus-visible': { outline: `3px solid ${focusRingColor}`, outlineOffset: '2px' },
      },
    };
  }

  // createTheme(theme, options) only runs @mui/system's own createSpacing()
  // against its first argument — a plain number in a later arg would just
  // get deepmerge-d over the existing spacing *function*, breaking every
  // theme.spacing(n) call site. Building the replacement function directly
  // (a no-op at contentSpacingFactor === 1) sidesteps that.
  const spacing =
    contentSpacingFactor === 1 ? baseTheme.spacing : createSpacing(spacingUnitPx(baseTheme) * contentSpacingFactor);

  return createTheme(baseTheme, {
    spacing,
    ...(paletteOverride ? { palette: paletteOverride } : {}),
    typography: typographyOverride,
    ...(preferences.reduceMotion ? { transitions: { create: () => 'none' } } : {}),
    components,
  });
}
